import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Grid,
} from "@material-ui/core";
import { Close as CloseIcon } from "@material-ui/icons";
import { makeStyles } from "@material-ui/styles";
import { useCrearRutAlternativoMutation } from "src/services/conciliacionApi";
import { useSnackbar } from "src/components/snackbar";
import { formatRut, validateRut, RutFormat } from "@fdograph/rut-utilities";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.primary.contrastText,
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
}));

export default function ModalRutAlternativo({
  open,
  onClose,
  clienteSeleccionado = null,
}) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  // Estados del formulario
  const [formData, setFormData] = useState({
    ref_cliente: "",
    nombre_cliente: "",
    rut_alternativo: "",
    descripcion: "",
  });

  const [errors, setErrors] = useState({
    rut_alternativo: "",
  });

  // RTK Query
  const [crearRut, { isLoading }] = useCrearRutAlternativoMutation();

  useEffect(() => {
    if (open && clienteSeleccionado) {
      setFormData((prev) => ({
        ...prev,
        ref_cliente: clienteSeleccionado.rut,
        nombre_cliente: clienteSeleccionado.nombre,
      }));
    }
  }, [open, clienteSeleccionado]);

  useEffect(() => {
    if (!open) {
      setFormData({
        ref_cliente: "",
        nombre_cliente: "",
        rut_alternativo: "",
        descripcion: "",
      });
      setErrors({
        rut_alternativo: "",
      });
    }
  }, [open]);

  // ========== HANDLERS ==========
  const handleClose = () => {
    if (!isLoading) {
      onClose(false);
    }
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Limpiar error del campo al empezar a escribir
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleRutBlur = () => {
    const rut = formData.rut_alternativo;
    if (!rut) return;

    try {
      // Intentar formatear el RUT
      const rutFormateado = formatRut(rut, RutFormat.DOTS_DASH);
      setFormData((prev) => ({
        ...prev,
        rut_alternativo: rutFormateado,
      }));
    } catch (error) {
      // Si no se puede formatear, dejar como está
      console.warn("No se pudo formatear el RUT:", error);
    }
  };

  const validateForm = () => {
    const newErrors = {
      rut_alternativo: "",
    };

    let isValid = true;

    // Validar RUT Alternativo
    if (!formData.rut_alternativo.trim()) {
      newErrors.rut_alternativo = "El RUT alternativo es requerido";
      isValid = false;
    } else if (!validateRut(formData.rut_alternativo)) {
      newErrors.rut_alternativo = "RUT alternativo inválido";
      isValid = false;
    }

    // Validar que no sea el mismo RUT del cliente
    if (
      isValid &&
      formData.ref_cliente.replace(/[.-]/g, "") ===
        formData.rut_alternativo.replace(/[.-]/g, "")
    ) {
      newErrors.rut_alternativo =
        "El RUT alternativo no puede ser igual al RUT del cliente";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      await crearRut({
        ref_cliente: formData.ref_cliente,
        rut_alternativo: formData.rut_alternativo,
        descripcion: formData.descripcion.trim() || undefined,
      }).unwrap();

      enqueueSnackbar("RUT alternativo creado exitosamente", {
        variant: "success",
      });

      onClose(true); // true = refrescar datos
    } catch (error) {
      console.error("Error al crear RUT alternativo:", error);

      // Manejo de errores específicos
      if (error?.status === 409) {
        enqueueSnackbar(
          "Este RUT alternativo ya está registrado para este cliente",
          { variant: "error" },
        );
      } else {
        enqueueSnackbar(
          error?.data?.message || "Error al crear RUT alternativo",
          { variant: "error" },
        );
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableBackdropClick={isLoading}
      disableEscapeKeyDown={isLoading}
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography variant="h6">Agregar RUT Alternativo</Typography>
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={handleClose}
          disabled={isLoading}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box>
          <Grid container spacing={2}>
            {/* RUT Cliente - DISABLED (pre-llenado) */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="RUT Cliente"
                value={
                  formData.ref_cliente
                    ? formatRut(formData.ref_cliente, RutFormat.DOTS_DASH)
                    : ""
                }
                disabled // ← CAMBIO: Siempre disabled
                helperText="RUT del cliente (automático)"
                className={classes.formField}
              />
            </Grid>

            {/* Nombre Cliente - NUEVO campo (disabled, solo lectura) */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nombre Cliente"
                value={formData.nombre_cliente}
                disabled // ← NUEVO: Campo solo lectura
                helperText="Nombre del cliente (automático)"
                className={classes.formField}
              />
            </Grid>
          </Grid>

          {/* RUT Alternativo - EDITABLE */}
          <TextField
            fullWidth
            required
            label="RUT Alternativo"
            placeholder="11.111.111-1"
            value={formData.rut_alternativo}
            onChange={handleChange("rut_alternativo")}
            onBlur={handleRutBlur}
            error={Boolean(errors.rut_alternativo)}
            helperText={
              errors.rut_alternativo ||
              "RUT relacionado (holding, sucursal, filial, etc.)"
            }
            disabled={isLoading}
            className={classes.formField}
          />

          {/* Descripción */}
          <TextField
            fullWidth
            label="Descripción"
            placeholder="Ej: Sucursal Santiago, Holding Matriz, etc."
            value={formData.descripcion}
            onChange={handleChange("descripcion")}
            multiline
            rows={3}
            helperText="Opcional: Describe la relación entre los RUTs"
            disabled={isLoading}
            className={classes.formField}
          />

          {/* Advertencia de impacto */}
          <Alert severity="info" variant="outlined">
            <Typography variant="body2">
              Este RUT alternativo se utilizará en el motor de matching
              automático para identificar pagos relacionados (+20 puntos).
            </Typography>
          </Alert>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isLoading ? "Guardando..." : "Guardar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
